"use client"

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

type CartDrawerContextValue = {
  isOpen: boolean
  recentItem: RecentItem | null
  open: (item?: RecentItem | null) => void
  close: () => void
}

type RecentItem = {
  title?: string
  image?: string | null
  variantLabel?: string
  quantity?: number
}

const CartDrawerContext = createContext<CartDrawerContextValue | undefined>(
  undefined
)

export const CartDrawerProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [recentItem, setRecentItem] = useState<RecentItem | null>(null)

  const open = useCallback((item?: RecentItem | null) => {
    if (item) {
      setRecentItem(item)
    }
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const value = useMemo(
    () => ({
      isOpen,
      recentItem,
      open,
      close,
    }),
    [close, isOpen, open, recentItem]
  )

  return (
    <CartDrawerContext.Provider value={value}>
      {children}
    </CartDrawerContext.Provider>
  )
}

export const useCartDrawer = () => {
  const context = useContext(CartDrawerContext)
  if (!context) {
    throw new Error("useCartDrawer must be used within a CartDrawerProvider")
  }
  return context
}

export type { RecentItem as CartDrawerRecentItem }
